/**
 * wp-slider.js — shared WeltPartner slider custom elements
 *
 * Consolidates five diverged copies of the same two behaviours (three inline
 * section scripts + two init functions in wp-handelspartner.js, one of which
 * was a verbatim twin of the inline script in wp-pdp-tab-handelspartner).
 *
 * Both elements are WRAPPERS around the existing markup and are styled
 * `display: contents` in src/app.css — they generate no box, so the rendered
 * layout (and every committed fidelity pixel baseline) is unchanged.
 *
 * ═══════════════════════════════════════════════════════════════
 * <wp-scroll-rail> — arrow-paged horizontal rail
 * ═══════════════════════════════════════════════════════════════
 *
 *   <wp-scroll-rail data-scroll-by="424">
 *     <div data-slider-controls>
 *       <button data-slider-prev aria-label="Zurück">…</button>
 *       <button data-slider-next aria-label="Weiter">…</button>
 *     </div>
 *     <div class="…__rail" data-slider-rail>…</div>
 *   </wp-scroll-rail>
 *
 * ATTRIBUTES
 * ──────────
 *   data-scroll-by="<px>"  — click paging distance. Default 600.
 *                            kategorie-slider 424, testimonial-slider 420.
 *
 * BEHAVIOUR (superset of the four former copies)
 * ──────────────────────────────────────────────
 *   • prev/next scroll the rail by data-scroll-by (smooth, `auto` under
 *     prefers-reduced-motion)
 *   • overflow detection: [data-slider-controls] is hidden when everything
 *     already fits
 *   • disabled state follows the rail's SCROLL POSITION, not just clicks, so
 *     swipe/trackpad updates the arrows too (Asana 1215586165913500);
 *     [disabled] styling lives in src/app.css
 *   • rAF-throttled passive scroll listener
 *   • the rail is looked up lazily and the scroll listener re-bound when it
 *     changes — <product-recommendations> injects its rail after load
 *   • MutationObserver + window load/resize keep the state in sync
 *
 * ═══════════════════════════════════════════════════════════════
 * <wp-image-slider> — looping, center-start, drag gallery
 * ═══════════════════════════════════════════════════════════════
 *
 *   <wp-image-slider>
 *     <div class="wp-image-slider">          ← note: CSS class, not the tag
 *       <div class="wp-image-slider__rail" data-slider-rail>
 *         <div class="wp-image-slider__slide">…</div> × n
 *       </div>
 *       <div class="wp-image-slider__footer">
 *         <button data-slider-prev aria-label="Zurück">…</button>
 *         <span data-slider-dot></span> × n
 *         <button data-slider-next aria-label="Weiter">…</button>
 *       </div>
 *     </div>
 *   </wp-image-slider>
 *
 * BEHAVIOUR (client feedback 1215586165913490)
 * ────────────────────────────────────────────
 *   Starts on the middle image and loops infinitely: the full slide set is
 *   cloned once on each side of the originals; whenever scrolling settles on
 *   a clone we teleport (instant scrollLeft shift of one set width) back to
 *   the matching original — native scroll-snap keeps doing the actual
 *   sliding. Mouse drag-to-scroll on top (touch uses native scrolling).
 *
 * ═══════════════════════════════════════════════════════════════
 */

(function () {
  'use strict';

  const REDUCED_MQ = window.matchMedia('(prefers-reduced-motion: reduce)');
  /* Read at call time, not at load time — the OS setting can change mid-session. */
  const scrollBehavior = () => (REDUCED_MQ.matches ? 'auto' : 'smooth');

  const DEFAULT_SCROLL_BY = 600;

  /* ── <wp-scroll-rail> ───────────────────────────────────────────────────── */
  class WpScrollRail extends HTMLElement {
    constructor() {
      super();
      this._raf = 0;
      this._boundRail = null;
      this._observer = null;
      this._boundSync = this._sync.bind(this);
      this._boundOnScroll = this._onScroll.bind(this);
    }

    connectedCallback() {
      this._ac = new AbortController();
      const { signal } = this._ac;

      this._controls = this.querySelector('[data-slider-controls]');

      const configured = parseInt(this.dataset.scrollBy, 10);
      this._scrollBy = Number.isFinite(configured) && configured > 0
        ? configured
        : DEFAULT_SCROLL_BY;

      this.querySelectorAll('[data-slider-prev]').forEach((b) => {
        b.addEventListener('click', () => this._page(-1), { signal });
      });
      this.querySelectorAll('[data-slider-next]').forEach((b) => {
        b.addEventListener('click', () => this._page(1), { signal });
      });

      window.addEventListener('resize', this._boundSync, { signal });
      window.addEventListener('load', this._boundSync, { signal });

      // childList only: _sync writes style/disabled ATTRIBUTES, which this
      // observer does not watch — no feedback loop.
      this._observer = new MutationObserver(this._boundSync);
      this._observer.observe(this, { childList: true, subtree: true });

      this._sync();
    }

    disconnectedCallback() {
      this._ac?.abort();
      this._observer?.disconnect();
      this._observer = null;
      if (this._raf) {
        cancelAnimationFrame(this._raf);
        this._raf = 0;
      }
      this._boundRail = null;
    }

    _page(direction) {
      const rail = this.querySelector('[data-slider-rail]');
      rail?.scrollBy({ left: direction * this._scrollBy, behavior: scrollBehavior() });
    }

    _onScroll() {
      if (this._raf) return;
      this._raf = requestAnimationFrame(() => {
        this._raf = 0;
        this._sync();
      });
    }

    _sync() {
      const rail = this.querySelector('[data-slider-rail]');
      if (!rail) return;

      const overflows = rail.scrollWidth > rail.clientWidth + 1;
      if (this._controls) this._controls.style.display = overflows ? '' : 'none';

      const maxScroll = rail.scrollWidth - rail.clientWidth;
      this.querySelectorAll('[data-slider-prev]').forEach((b) => {
        b.disabled = rail.scrollLeft <= 1;
      });
      this.querySelectorAll('[data-slider-next]').forEach((b) => {
        b.disabled = rail.scrollLeft >= maxScroll - 1;
      });

      // Bind lazily: <product-recommendations> swaps its rail in after load.
      if (this._boundRail !== rail) {
        this._boundRail = rail;
        rail.addEventListener('scroll', this._boundOnScroll, {
          passive: true,
          signal: this._ac.signal,
        });
      }
    }
  }

  /* ── <wp-image-slider> ──────────────────────────────────────────────────── */
  class WpImageSlider extends HTMLElement {
    constructor() {
      super();
      this._cloned = false;
      this._setCount = 0;
      this._settleTimer = 0;
    }

    connectedCallback() {
      const rail = this.querySelector('[data-slider-rail]');
      if (!rail) return;
      const dots = this.querySelectorAll('[data-slider-dot]');

      const firstInit = !this._cloned;
      if (firstInit) {
        const originals = [...rail.querySelectorAll('.wp-image-slider__slide')];
        if (!originals.length) return;
        this._setCount = originals.length;
        originals.forEach((s) => {
          const head = s.cloneNode(true);
          const tail = s.cloneNode(true);
          head.setAttribute('aria-hidden', 'true');
          tail.setAttribute('aria-hidden', 'true');
          rail.insertBefore(head, originals[0]);
          rail.appendChild(tail);
        });
        this._cloned = true;
      }

      this._ac = new AbortController();
      const { signal } = this._ac;

      const n = this._setCount;
      const slides = rail.querySelectorAll('.wp-image-slider__slide');

      // Drag state is declared up front: teleport() reads `dragging`.
      let dragging = false;
      let moved = false;
      let startX = 0;
      let startScroll = 0;

      const setWidth = () => slides[n].offsetLeft - slides[0].offsetLeft;
      const centerLeft = (slide) => slide.offsetLeft + slide.offsetWidth / 2 - rail.clientWidth / 2;
      const nearestIndex = () => {
        const center = rail.scrollLeft + rail.clientWidth / 2;
        let best = 0;
        let bestDist = Infinity;
        slides.forEach((slide, i) => {
          const dist = Math.abs(slide.offsetLeft + slide.offsetWidth / 2 - center);
          if (dist < bestDist) { bestDist = dist; best = i; }
        });
        return best;
      };
      const setActive = (i) => dots.forEach((d, k) => d.classList.toggle('is-active', k === i % n));
      const step = (dir) => {
        const target = slides[nearestIndex() + dir];
        if (target) rail.scrollTo({ left: centerLeft(target), behavior: scrollBehavior() });
      };
      this.querySelector('[data-slider-prev]')?.addEventListener('click', () => step(-1), { signal });
      this.querySelector('[data-slider-next]')?.addEventListener('click', () => step(1), { signal });

      let ticking = false;
      const sync = () => {
        ticking = false;
        setActive(nearestIndex());
      };
      // Once a scroll settles on a clone, jump silently to its original.
      // Guarded while dragging — endDrag's glide settles again afterwards.
      const teleport = () => {
        if (dragging) return;
        const i = nearestIndex();
        if (i >= n && i < 2 * n) return;
        rail.scrollLeft += i < n ? setWidth() : -setWidth();
      };
      if ('onscrollend' in rail) rail.addEventListener('scrollend', teleport, { signal });
      rail.addEventListener('scroll', () => {
        if (!('onscrollend' in rail)) {
          clearTimeout(this._settleTimer);
          this._settleTimer = setTimeout(teleport, 150);
        }
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(sync);
      }, { signal });

      if (firstInit) {
        // Start centered on the middle slide of the original set.
        const start = n + Math.floor(n / 2);
        rail.scrollLeft = centerLeft(slides[start]);
        setActive(start);
      }

      // Mouse drag-to-scroll (touch uses native scrolling).
      rail.addEventListener('pointerdown', (e) => {
        if (e.pointerType === 'touch') return;
        dragging = true; moved = false;
        startX = e.clientX; startScroll = rail.scrollLeft;
        rail.style.scrollSnapType = 'none';
        rail.setPointerCapture(e.pointerId);
      }, { signal });
      rail.addEventListener('pointermove', (e) => {
        if (!dragging) return;
        const dx = e.clientX - startX;
        if (Math.abs(dx) > 3) moved = true;
        rail.scrollLeft = startScroll - dx;
      }, { signal });
      const nearestSlideLeft = () => {
        const center = rail.scrollLeft + rail.clientWidth / 2;
        let best = slides[0];
        let bestDist = Infinity;
        slides.forEach((slide) => {
          const dist = Math.abs(slide.offsetLeft + slide.offsetWidth / 2 - center);
          if (dist < bestDist) { bestDist = dist; best = slide; }
        });
        return best.offsetLeft + best.offsetWidth / 2 - rail.clientWidth / 2;
      };
      const endDrag = (e) => {
        if (!dragging) return;
        dragging = false;
        if (rail.hasPointerCapture?.(e.pointerId)) rail.releasePointerCapture(e.pointerId);
        // Snap stays off so the browser doesn't jump; glide to the nearest
        // slide, then re-enable mandatory snap once we've arrived.
        rail.scrollTo({ left: nearestSlideLeft(), behavior: scrollBehavior() });
        const restore = () => { rail.style.scrollSnapType = ''; rail.removeEventListener('scrollend', restore); };
        if ('onscrollend' in rail) rail.addEventListener('scrollend', restore, { signal });
        else setTimeout(restore, 450);
      };
      rail.addEventListener('pointerup', endDrag, { signal });
      rail.addEventListener('pointercancel', endDrag, { signal });
      // Suppress click after a drag (e.g. on slide images/links).
      rail.addEventListener('click', (e) => {
        if (moved) { e.preventDefault(); e.stopPropagation(); }
      }, { capture: true, signal });
      rail.addEventListener('dragstart', (e) => e.preventDefault(), { signal });
    }

    disconnectedCallback() {
      this._ac?.abort();
      clearTimeout(this._settleTimer);
      this._settleTimer = 0;
    }
  }

  if (!customElements.get('wp-scroll-rail')) customElements.define('wp-scroll-rail', WpScrollRail);
  if (!customElements.get('wp-image-slider')) customElements.define('wp-image-slider', WpImageSlider);
})();
